import { BrightcovePlayer } from '@brightcove/player-loader';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DiamondIcon from '@moda/icons/diamond-16';
import { Clickable } from '@moda/om';
import { SECONDS_TO_MILLISECONDS } from '@moda/portal-stanchions';

import './VideoProgressBar.scss';

const DIAMOND_SIZE = 16;

const PROGRESS_DECIMAL_PRECISION = 4;

interface Props {
  player: BrightcovePlayer | null;
  show?: boolean;
}

export const VideoProgressBar: React.FC<Props> = ({ player, show = true }) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [progress, setProgress] = useState<undefined | number>();

  const duration = player?.catalog.data?.duration
    ? player.catalog.data.duration / SECONDS_TO_MILLISECONDS
    : undefined;

  const containerWidth = ref.current?.getBoundingClientRect().width;

  useEffect(() => {
    if (!player) return;

    player.on('timeupdate', () => {
      if (duration === undefined) return;

      if (isNaN(duration)) return;

      const currentTime = player.currentTime();

      if (!currentTime) return;

      const totalProgress = parseFloat(
        (currentTime / duration).toFixed(PROGRESS_DECIMAL_PRECISION)
      );

      setProgress(totalProgress);
    });
  }, [duration, player]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();

      if (!player) return;

      if (!duration) return;

      if (isNaN(duration)) return;

      if (!containerWidth) return;

      const { clientX } = event;

      const node = event.target as HTMLButtonElement;

      const { left } = node.getBoundingClientRect();

      const clickedProgress = (clientX - left) / containerWidth;

      player.currentTime(clickedProgress * duration);
    },
    [containerWidth, duration, player]
  );

  const handleDiamondClick = useCallback(
    (
      event: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => {
      event?.preventDefault();
      event?.stopPropagation();
    },
    []
  );

  if (progress === undefined) return null;

  return (
    <Clickable
      aria-label="restart video"
      className={classNames('VideoProgressBar', {
        'VideoProgressBar--hidden': !show
      })}
      onKeyDown={undefined}
      onClick={handleClick}
      ref={ref}
    >
      <span
        className="VideoProgressBar__diamond"
        onKeyDown={handleDiamondClick}
        onClick={handleDiamondClick}
        style={{
          transform: containerWidth
            ? `translate(${progress * containerWidth - DIAMOND_SIZE / 2}px)`
            : undefined
        }}
        role="button"
        tabIndex={-1}
      >
        <DiamondIcon height={`${DIAMOND_SIZE}px`} width={`${DIAMOND_SIZE}px`} />
      </span>
    </Clickable>
  );
};
